import axios from 'axios';

// Learn backend public API (no auth required)
// VITE_LEARN_API_BASE_URL already includes /api/learn (e.g. http://localhost:5002/api/learn)
const LEARN_PUBLIC_BASE =
  (import.meta.env.VITE_LEARN_API_BASE_URL || 'http://localhost:5002/api/learn').replace(/\/+$/, '') +
  '/v1/public/training';

const learnPublicApi = axios.create({
  baseURL: LEARN_PUBLIC_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

learnPublicApi.interceptors.response.use(
  (res) => res,
  (error) => {
    console.error('Learn Public API Error:', error.response?.status, error.config?.url);
    return Promise.reject(error);
  },
);

// ---------- Types ----------

export interface CatalogEvent {
  id: number;
  event_id: number;
  event_name: string;
  course_title: string;
  course_description: string | null;
  course_thumbnail: string | null;
  course_type: string | null;
  event_mode: string | null;
  external_trainer: string | null;
  external_location: string | null;
  registration_restriction: string;
  show_in_catalog: boolean;
  shareable_token: string;
  link_enabled: boolean;
  auto_approve_external: boolean;
  allow_waitlist: boolean;
  registration_closes_before_days: number;
  sessions: CatalogSession[];
}

export interface CatalogSession {
  id: number;
  session_name: string;
  start_date: string;
  start_time: string;
  end_date: string;
  mode: string;
  location_name: string;
  trainer_name: string;
  max_participants: number;
  enrolled_count: number;
  available_spots: number;
}

export interface RegistrationRequest {
  email: string;
  first_name: string;
  last_name?: string;
  phone?: string;
  organization_name?: string;
  job_title?: string;
  consent_given: boolean;
  share_token?: string;
}

export interface RegistrationResult {
  participant_id: number;
  external_learner_id: number;
  registration_status: string;
  magic_token: string;
  cancel_token: string;
  access_token: string;
}

export interface PortalLearner {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  organization_name: string;
  magic_token: string;
}

export interface PortalRegistration {
  participant_id: number;
  ilt_session_id: number;
  session_name: string;
  event_name: string;
  start_date: string;
  start_time: string;
  end_time: string | null;
  location_name: string;
  status: string;
  cancel_token: string;
  event_id: number | null;
  event_mode: string;
  course_name: string;
  platform: string;
  duration_minutes: number | null;
  attended: boolean | null;
}

// ---------- Enrollment & Step Progress Types ----------

export interface PortalEnrollment {
  enrollment_id: number;
  enrollment_code: string | null;
  event_id: number;
  session_id: number;
  enrollment_status: string;
  registered_on: string | null;
  event_name: string;
  event_code: string | null;
  event_mode: string;
  session_name: string | null;
  session_start_date: string | null;
  session_end_date: string | null;
  session_start_time: string | null;
  session_end_time: string | null;
  location_name: string;
  course_name: string;
  course_thumbnail: string;
  external_learner_id: number | null;
  access_token: string | null;
}

export interface StepProgress {
  id: number;
  step_id: number;
  status: 'not_started' | 'in_progress' | 'completed' | 'skipped' | 'failed';
  progress_percentage: number | null;
  started_at: string | null;
  completed_at: string | null;
  step_name: string;
  step_description: string | null;
  step_type: string | null;
  step_order: number | null;
  step_start_date: string | null;
  step_due_date: string | null;
  step_attachment: Record<string, unknown> | null;
}

export interface OrgBrandingInfo {
  domain: string;
  company_name: string;
  logo: string;
}

export interface SessionDay {
  id: number;
  day_number: number | null;
  day_date: string | null;
  start_time: string | null;
  end_time: string | null;
  all_day: boolean;
  ilt_platform: string;
  ilt_join_url: string;
  ilt_meeting_status: string;
  attendance_status?: string;
}

export interface RegistrationDetail {
  enrollment: {
    id: number;
    enrollment_code: string | null;
    event_id: number;
    session_id: number;
    enrollment_status: string;
    registered_on: string | null;
    attended_on: string | null;
    event_name?: string;
    event_mode?: string;
    session_name?: string;
    course_name?: string;
    location_name?: string;
    session_start_date?: string | null;
    session_end_date?: string | null;
    session_start_time?: string | null;
    session_end_time?: string | null;
  };
  steps: StepProgress[];
  session_days?: SessionDay[];
  organization?: OrgBrandingInfo;
}

export interface StepSummary {
  total_steps: number;
  completed_steps: number;
  in_progress_steps: number;
  not_started_steps: number;
  failed_steps: number;
  skipped_steps: number;
}

// ---------- API Functions ----------

/** Browse the public catalog. Pass org domain for subdomain-filtered results. */
export async function fetchCatalogEvents(limit = 20, offset = 0, org?: string) {
  const params: Record<string, unknown> = { limit, offset };
  if (org) params.org = org;
  const res = await learnPublicApi.get('/catalog', { params });
  return res.data?.data || res.data;
}

/** Get org branding by domain (for subdomain portal hero) */
export async function fetchOrgBranding(domain: string) {
  const res = await learnPublicApi.get(`/org/${domain}`);
  return res.data?.data || res.data;
}

/** Get event detail by ID (from catalog) */
export async function fetchEventDetail(eventId: number) {
  const res = await learnPublicApi.get(`/events/${eventId}/detail`);
  return res.data?.data || res.data;
}

/** Get event detail by shareable token (link-only access) */
export async function fetchEventByToken(token: string) {
  const res = await learnPublicApi.get(`/events/share/${token}`);
  return res.data?.data || res.data;
}

/** Register for a session */
export async function registerForSession(
  eventId: number,
  sessionId: number,
  data: RegistrationRequest,
): Promise<RegistrationResult> {
  const res = await learnPublicApi.post(`/events/${eventId}/sessions/${sessionId}/register`, data);
  return res.data?.data || res.data;
}

/** Batch register multiple people for a session */
export async function batchRegisterForSession(
  eventId: number,
  sessionId: number,
  registrants: RegistrationRequest[],
): Promise<{ email: string; success: boolean; error?: string; result?: RegistrationResult }[]> {
  const res = await learnPublicApi.post(`/events/${eventId}/sessions/${sessionId}/register-batch`, { registrants });
  return res.data?.data || res.data;
}

/** Get learner portal data via magic token */
export async function fetchLearnerPortal(magicToken: string) {
  const res = await learnPublicApi.get('/portal', { params: { t: magicToken } });
  return res.data?.data || res.data;
}

/** Cancel a registration via cancel token */
export async function cancelRegistration(cancelToken: string) {
  const res = await learnPublicApi.post(`/cancel/${cancelToken}`);
  return res.data?.data || res.data;
}

// ---------- Enrollment & Step Progress API Functions ----------

/** Get all session enrollments for an external learner (v2 portal with step tracking) */
export async function fetchLearnerEnrollments(magicToken: string): Promise<{
  learner: PortalLearner;
  enrollments: PortalEnrollment[];
  organization?: OrgBrandingInfo;
}> {
  const res = await learnPublicApi.get('/portal/enrollments', { params: { t: magicToken } });
  return res.data?.data || res.data;
}

/** Get enrollment detail with step progress */
export async function fetchRegistrationDetail(
  enrollmentId: number,
  magicToken: string,
): Promise<RegistrationDetail> {
  const res = await learnPublicApi.get(`/portal/registration/${enrollmentId}`, {
    params: { t: magicToken },
  });
  return res.data?.data || res.data;
}

/** Get step completion summary for an enrollment */
export async function fetchStepSummary(
  enrollmentId: number,
  magicToken: string,
): Promise<StepSummary> {
  const res = await learnPublicApi.get(`/portal/registration/${enrollmentId}/summary`, {
    params: { t: magicToken },
  });
  return res.data?.data || res.data;
}

/** Update step status (in_progress or completed) */
export async function updateStepStatus(
  progressId: number,
  status: 'in_progress' | 'completed',
  magicToken: string,
) {
  const res = await learnPublicApi.put(
    `/portal/steps/${progressId}/status`,
    { status },
    { params: { t: magicToken } },
  );
  return res.data?.data || res.data;
}

/** Update step progress percentage (0-100) */
export async function updateStepProgress(
  progressId: number,
  progressPercentage: number,
  magicToken: string,
) {
  const res = await learnPublicApi.put(
    `/portal/steps/${progressId}/progress`,
    { progress_percentage: progressPercentage },
    { params: { t: magicToken } },
  );
  return res.data?.data || res.data;
}

/** Submit work for a TASK/ASSESSMENT step */
export async function submitStepWork(
  progressId: number,
  submissionUrl: string,
  submissionData: Record<string, unknown> | null,
  token: string,
) {
  const res = await learnPublicApi.post(
    `/portal/steps/${progressId}/submit`,
    { submission_url: submissionUrl, submission_data: submissionData },
    { params: { t: token } },
  );
  return res.data?.data || res.data;
}

export default learnPublicApi;
